import { useState } from "react";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, ChevronLeft, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  {
    id: "frequency",
    question: "How often do you currently get a haircut?",
    subtext: "This helps us recommend the right membership for your lifestyle.",
    options: [
      { value: "weekly", label: "Every week", desc: "I like staying fresh" },
      { value: "biweekly", label: "Every 2 weeks", desc: "The sweet spot" },
      { value: "monthly", label: "Once a month", desc: "Low maintenance" },
      { value: "rarely", label: "Less than monthly", desc: "Only when needed" },
    ],
  },
  {
    id: "frustration",
    question: "What's your biggest frustration with getting haircuts?",
    subtext: "We built LUXGROOM to solve exactly this.",
    options: [
      { value: "time", label: "Finding the time", desc: "My schedule is packed" },
      { value: "consistency", label: "Inconsistent quality", desc: "Every visit feels different" },
      { value: "waiting", label: "Long wait times", desc: "I hate sitting around" },
      { value: "travel", label: "Traveling to a shop", desc: "The commute kills me" },
    ],
  },
  {
    id: "convenience",
    question: "How important is convenience in your grooming routine?",
    subtext: "Be honest. There's no wrong answer.",
    options: [
      { value: "critical", label: "It's everything", desc: "I need it at my door" },
      { value: "very", label: "Very important", desc: "I'll pay for time saved" },
      { value: "moderate", label: "Somewhat important", desc: "Nice to have" },
      { value: "low", label: "Not a priority", desc: "I'll go wherever is best" },
    ],
  },
  {
    id: "services",
    question: "What grooming services matter most to you?",
    subtext: "All included in every LUXGROOM membership.",
    options: [
      { value: "fade", label: "Precision fade & lineup", desc: "Clean, sharp edges" },
      { value: "beard", label: "Beard shaping & trim", desc: "Full beard maintenance" },
      { value: "treatment", label: "Hot towel & products", desc: "The full experience" },
      { value: "all", label: "All of the above", desc: "I want the works" },
    ],
  },
  {
    id: "schedule",
    question: "When do you prefer your grooming appointments?",
    subtext: "We'll match you with the perfect time slot.",
    options: [
      { value: "morning", label: "Early morning", desc: "Before 9am, start fresh" },
      { value: "midday", label: "Lunch break", desc: "Quick midday refresh" },
      { value: "evening", label: "Evening", desc: "After work wind-down" },
      { value: "weekend", label: "Weekends", desc: "Saturday or Sunday" },
    ],
  },
];

function getRecommendation(answers) {
  const freq = answers.frequency;
  const conv = answers.convenience;
  
  if (freq === "weekly" || conv === "critical") {
    return {
      plan: "weekly",
      title: "Weekly Elite",
      price: "$250/mo",
      reason: "Based on your answers, you value consistency and convenience. The Weekly Elite plan gives you a fresh cut every week.",
    };
  }
  if (freq === "biweekly" || conv === "very") {
    return {
      plan: "biweekly",
      title: "Bi-Weekly Pro",
      price: "$140/mo",
      reason: "Based on your answers, the Bi-Weekly Pro plan is the perfect balance of quality and value for your lifestyle.",
    };
  }
  return {
    plan: "monthly",
    title: "Monthly Essentials",
    price: "$100/mo",
    reason: "Based on your answers, the Monthly Essentials plan is a great way to get started with 1 haircut and 1 shape-up per month.",
  };
}

export default function QuizFunnel({ onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const totalSteps = questions.length;
  const progress = recommendation ? 100 : ((step) / totalSteps) * 100;
  const currentQ = questions[step];

  const handleSelect = (value) => {
    const newAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(newAnswers);

    if (step < totalSteps - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      const rec = getRecommendation(newAnswers);
      setRecommendation(rec);
      // Save quiz to backend
      axios.post(`${API}/quiz`, {
        answers: newAnswers,
        recommended_plan: rec.plan,
      }).catch(() => {});
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleCTA = () => {
    setSubmitted(true);
    toast.success("Great choice! Let's get you booked.");
    if (onComplete) onComplete(recommendation?.plan);
  };

  return (
    <section data-testid="quiz-section" className="relative py-24 md:py-32 bg-[#0A0A0B]">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
              Find Your Perfect Plan
            </span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-4">
            5 quick questions. Your ideal membership.
          </h2>
          <p className="text-base text-[#A1A1AA] leading-relaxed font-light">
            Answer a few questions about your lifestyle and we'll recommend the perfect LUXGROOM plan for you.
          </p>
        </div>

        {/* Quiz Container */}
        <div className="max-w-2xl">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-[#71717A]">
                {recommendation ? "Complete" : `Question ${step + 1} of ${totalSteps}`}
              </span>
              <span className="text-sm text-[#C5A059] font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress 
              value={progress} 
              className="h-1.5 bg-white/5"
              data-testid="quiz-progress"
            />
          </div>

          {/* Question or Result */}
          {!recommendation ? (
            <div className="animate-fade-in" key={step}>
              <h3 className="font-heading text-xl sm:text-2xl tracking-tight font-medium text-[#F3F4F6] mb-2">
                {currentQ.question}
              </h3>
              <p className="text-sm text-[#71717A] mb-8">{currentQ.subtext}</p>

              <div className="space-y-3">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`quiz-option-${currentQ.id}-${opt.value}`}
                    onClick={() => handleSelect(opt.value)}
                    className={`quiz-option w-full text-left p-5 rounded-md border border-white/10 bg-[#141416] flex items-center justify-between group ${
                      answers[currentQ.id] === opt.value ? "selected" : ""
                    }`}
                  >
                    <div>
                      <p className="text-[#F3F4F6] font-medium text-sm mb-1">{opt.label}</p>
                      <p className="text-[#71717A] text-xs">{opt.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#71717A] group-hover:text-[#C5A059] transition-colors" />
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  data-testid="quiz-back-btn"
                  onClick={handleBack}
                  className="mt-6 flex items-center gap-2 text-sm text-[#71717A] hover:text-[#C5A059] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous question
                </button>
              )}
            </div>
          ) : (
            <div className="animate-scale-in" data-testid="quiz-result">
              <div className="p-8 md:p-10 rounded-md border border-[#C5A059]/30 bg-[#141416] glow-gold">
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle className="w-6 h-6 text-[#C5A059]" />
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                    Your Recommendation
                  </span>
                </div>

                <h3 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-2">
                  {recommendation.title}
                </h3>
                <p className="font-heading text-2xl text-[#C5A059] font-medium mb-4">
                  {recommendation.price}
                </p>
                <p className="text-[#A1A1AA] text-base leading-relaxed font-light mb-8">
                  {recommendation.reason}
                </p>

                {!submitted ? (
                  <div className="flex flex-wrap gap-4">
                    <button
                      data-testid="quiz-claim-btn"
                      onClick={handleCTA}
                      className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
                    >
                      Claim This Plan
                    </button>
                    <button
                      data-testid="quiz-retake-btn"
                      onClick={() => {
                        setStep(0);
                        setAnswers({});
                        setRecommendation(null);
                        setSubmitted(false);
                      }}
                      className="bg-transparent border border-white/20 text-white hover:border-[#C5A059] transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
                    >
                      Retake Quiz
                    </button>
                  </div>
                ) : (
                  <p className="text-[#C5A059] font-medium text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Scroll down to complete your booking
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="section-divider mt-24 mx-6 md:mx-12 lg:mx-24" />
    </section>
  );
}
